// variablesTexto.js — Variables tipo {nombre} dentro de los textos de secciones.
// Sirve para saludos personalizados ("Bienvenid@ Dr(a). {nombre}") sin tocar
// código: el admin escribe la variable en el texto y aquí se reemplaza por el
// dato del usuario que está viendo la página. Si el texto no trae ninguna
// variable no se hace nada (ni se pide el perfil), así que nada cambia donde no
// se use.
import { useEffect, useState } from 'react';
import { profileService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Variables disponibles (se muestran también en el formulario del admin).
export const VARIABLES_TEXTO = [
  { token: '{nombre}', desc: 'Nombre del usuario' },
  { token: '{primer_nombre}', desc: 'Solo la primera palabra del nombre' },
  { token: '{email}', desc: 'Correo' },
  { token: '{especialidad}', desc: 'Especialidad' },
  { token: '{pais}', desc: 'País' },
  { token: '{telefono}', desc: 'Teléfono' },
  { token: '{convenio}', desc: 'Convenio / institución' },
];

const RE_VARIABLE = /\{([a-z_]+)\}/g;

export const tieneVariables = (...textos) =>
  textos.some(t => typeof t === 'string' && /\{[a-z_]+\}/.test(t));

// El valor se inserta en HTML, así que se escapa (el nombre lo escribe el propio
// usuario en su perfil).
const escaparHtml = (v) => String(v ?? '').replace(/[&<>"]/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

export const aplicarVariables = (texto, datos) => {
  if (typeof texto !== 'string' || !texto) return texto;
  return texto.replace(RE_VARIABLE, (m, clave) => (
    Object.prototype.hasOwnProperty.call(datos, clave) ? escaparHtml(datos[clave]) : m
  ));
};

// El login solo devuelve nombre/email/rol; el resto (especialidad, convenio…)
// viene del perfil. Se pide una sola vez por usuario y se comparte entre
// secciones.
let cachePerfil = { id: null, datos: null };

export const useVariablesUsuario = (activo) => {
  const { user } = useAuth();
  const [perfil, setPerfil] = useState(() => (cachePerfil.id === user?.id ? cachePerfil.datos : null));

  useEffect(() => {
    if (!activo || !user) return undefined;
    if (cachePerfil.id === user.id) { setPerfil(cachePerfil.datos); return undefined; }
    let vivo = true;
    profileService.getProfile()
      .then(res => {
        cachePerfil = { id: user.id, datos: res.data?.user || null };
        if (vivo) setPerfil(cachePerfil.datos);
      })
      .catch(() => { /* sin perfil se usa lo que trae el login */ });
    return () => { vivo = false; };
  }, [activo, user]);

  const u = { ...(user || {}), ...(perfil || {}) };
  const nombre = (u.nombre || '').trim();
  return {
    nombre,
    primer_nombre: nombre.split(/\s+/)[0] || '',
    email: u.email || '',
    especialidad: u.especialidad || '',
    pais: u.pais || '',
    telefono: u.telefono || '',
    convenio: u.convenio_nombre || '',
  };
};
